/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { PureComponent } from 'react';
import { arrayOf, shape, string, number, func } from 'prop-types';
import imagesLoaded from 'imagesloaded';
import classNames from 'clsx';

import { getWebPImage } from 'utils/image';

import Spinner from 'components/Spinner/Spinner';

import ArrowIcon from 'gfx/vector/arrow-icon.svg';

import styles from './image-modal.scss';

/**
 * This is the ImageModal component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export default class ImageModal extends PureComponent {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    images: arrayOf(
      shape({
        large_url: string.isRequired,
        index: number.isRequired,
      }).isRequired
    ).isRequired,
    index: number.isRequired,
    onClose: func,
  };

  static defaultProps = {
    onClose: () => {},
  };

  static imageLoader = null;

  /**
   * Create ImageModal.
   */
  constructor(props) {
    super(props);

    const { index } = this.props;

    this.state = {
      loading: true,
      index,
    };
  }

  componentDidMount() {
    this.loadImage();

    window.addEventListener('keydown', this.onKeyDown, true);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeyDown, true);
  }

  loadImage = () => {
    this.imageLoader = new imagesLoaded(this.modal, () => {
      // if modal is still there.
      if (this.modal) {
        this.preloadNearImages();

        this.setState({ loading: false });
      }
    });
  };

  onKeyDown = event => {
    if (event.defaultPrevented) {
      return; // Do nothing if the event was already processed
    }

    const { onClose } = this.props;

    switch (event.key) {
      case 'ArrowLeft':
        this.previousImage();
        break;
      case 'ArrowRight':
        this.nextImage();
        break;
      case 'Escape':
        onClose();
        break;
      default:
        return;
    }

    event.preventDefault();
  };

  previousImage = () => {
    this.setNewIndex(-1);
  };

  nextImage = () => {
    this.setNewIndex(1);
  };

  preloadNearImages = () => {
    const { images } = this.props;
    const { index } = this.state;
    const total = images.length - 1;
    const currentImage = images.find(image => image.index === index);
    const arrayIndex = Array.from(images).indexOf(currentImage);

    let prevIndex = arrayIndex - 1;
    prevIndex = prevIndex < 0 ? total : prevIndex;

    let nextIndex = arrayIndex + 1;
    nextIndex = nextIndex > total ? 0 : nextIndex;

    const { large_url: prevImage } = images[prevIndex];
    const { large_url: nextImage } = images[nextIndex];

    this.preloadImage(getWebPImage(prevImage));
    this.preloadImage(getWebPImage(nextImage));
  };

  preloadImage = src => {
    const preloader = new Image();

    preloader.src = src;
  };

  setNewIndex(direction) {
    const { images } = this.props;
    const { index } = this.state;
    const total = images.length - 1;

    const currentImage = images.find(image => image.index === index);
    const arrayIndex = Array.from(images).indexOf(currentImage);

    const restart = total < arrayIndex + direction;
    const newIndex = restart
      ? 0
      : 0 > arrayIndex + direction
      ? total
      : arrayIndex + direction;

    this.setState({
      index: images[newIndex].index,
      loading: true,
    });

    this.loadImage();
  }

  /**
   * Render ImageModal.
   *
   * @return {Object}
   */
  render() {
    const { onClose, images } = this.props;
    const { loading, index } = this.state;

    // We do not use the index of the image in array
    // but instead it's assigned array.
    const { large_url } = images.find(image => image.index === index);

    return (
      <div
        ref={ref => {
          this.modal = ref;
        }}
        className={classNames(styles.wrapper, {
          [styles.loading]: loading,
        })}
      >
        <button className={styles.close} onClick={onClose} />

        <button className={styles.prevButton} onClick={this.previousImage}>
          <ArrowIcon />
        </button>

        <div className={styles.content}>
          <img src={getWebPImage(large_url)} alt="" className={styles.image} />
        </div>

        <button className={styles.nextButton} onClick={this.nextImage}>
          <ArrowIcon />
        </button>

        {loading && (
          <div className={styles.spinner}>
            <Spinner medium />
          </div>
        )}
      </div>
    );
  }
}
